import * as React from 'react';
import { cn } from '@/lib/utils';
import defaultImage from '@/assets/images/placeholders/show-course.jpg';

const CourseCardAdminWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-wrapper',
      'grid gap-5',
      // Mobile: 1 coluna ocupando todo o espaço (evita o quebra de 328px em telas de 320px)
      'grid-cols-1',
      // Em telas pequenas/médias (sm), começamos a aplicar o comportamento de auto-fill
      'sm:grid-cols-[repeat(auto-fill,minmax(328px,1fr))]',
      // Em telas grandes (lg), aumentamos o tamanho mínimo do card
      'lg:grid-cols-[repeat(auto-fill,minmax(400px,1fr))]',
      className
    )}
    tabIndex={0}
    {...props}
  />
));
CourseCardAdminWrapper.displayName = 'CourseCardAdminWrapper';

const CourseCardAdmin = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin',
      // styles
      'group relative flex flex-col flex-wrap gap-5 rounded-xl bg-white p-5 shadow-[0px_4px_10px_rgba(119,119,120,0.05)] md:flex-row',
      className
    )}
    {...props}
  />
));
CourseCardAdmin.displayName = 'CourseCardAdmin';

const CourseCardAdminImageWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-img-wrapper',
      // styles
      'relative mx-auto min-h-[144px] w-36 flex-shrink-0 overflow-hidden rounded-xl',
      className
    )}
    {...props}
  />
));
CourseCardAdminImageWrapper.displayName = 'CourseCardAdminImageWrapper';

const CourseCardAdminImage = React.forwardRef(({ className, src, ...props }, ref) => (
  <img
    ref={ref}
    className={cn(
      'c-course-card-admin-img',
      // styles
      'h-full w-full object-cover transition-transform !duration-1000 ease-in-out group-hover:scale-125 group-focus:scale-125',
      className
    )}
    src={src || defaultImage}
    {...props}
  />
));
CourseCardAdminImage.displayName = 'CourseCardAdminImage';

const CourseCardAdminContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-content',
      // styles
      'flex w-full max-w-full flex-grow flex-col gap-6 md:w-36',
      className
    )}
    {...props}
  />
));
CourseCardAdminContent.displayName = 'CourseCardAdminContent';

const CourseCardAdminHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-header',
      // styles
      'flex flex-col gap-2 [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminHeader.displayName = 'CourseCardAdminHeader';

const CourseCardAdminHeaderContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-header-content',
      // styles
      'flex items-start gap-1',
      className
    )}
    {...props}
  />
));
CourseCardAdminHeaderContent.displayName = 'CourseCardAdminHeaderContent';

const CourseCardAdminTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'c-course-card-admin-title',
      // styles
      'mb-0 cursor-default truncate break-all text-base font-bold leading-[1.2] text-primary-dark',
      className
    )}
    {...props}
  />
));
CourseCardAdminTitle.displayName = 'CourseCardAdminTitle';

const CourseCardAdminBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-body',
      // styles
      'flex-grow [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminBody.displayName = 'CourseCardAdminBody';

const CourseCardAdminFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-course-card-admin-footer',
      // styles
      'flex items-center gap-5 [&>*:last-child]:mb-0',
      className
    )}
    {...props}
  />
));
CourseCardAdminFooter.displayName = 'CourseCardAdminFooter';

const CourseCardAdminRating = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      'c-course-card-admin-rating',
      // styles
      'mb-0 flex flex-none items-center gap-1 whitespace-nowrap text-sm font-bold text-[#5D6A86]',
      '[&_span]:text-xs [&_span]:font-light [&_span]:text-slate-500',
      '[&_svg]:min-w-[16px] [&_svg]:fill-[#FFD661] [&_svg]:stroke-[#FFD661]',
      className
    )}
    {...props}
  />
));
CourseCardAdminRating.displayName = 'CourseCardAdminRating';

export {
  // exports
  CourseCardAdminWrapper,
  CourseCardAdmin,
  CourseCardAdminImageWrapper,
  CourseCardAdminImage,
  CourseCardAdminContent,
  CourseCardAdminHeader,
  CourseCardAdminHeaderContent,
  CourseCardAdminTitle,
  CourseCardAdminBody,
  CourseCardAdminFooter,
  CourseCardAdminRating
};
